import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AssignFieldModal from '/@/components/field-list-v2/components/sidebar/components/assign-field-modal';
import useUnitProvider from '/@/core/context/unit';
import { useFieldsDashboard } from '/@/hooks/dashboards';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { getNodeScopeFilters, type NodeScopeFilters } from '/@/utils/node-scope';
import DashboardCard from '../../../../../components/bayer/dashboard-card';
import NewFieldGrowersModal from '../../../growers/components/new-field-modal';

const FieldsDashboardCard = ({
    userNodeId,
    showNewFieldButton = false,
    nodeScope = getNodeScopeFilters('myFields'),
    showAssignFieldsButton = false,
    assignFieldsButtonId,
}: {
    userNodeId: number;
    showNewFieldButton?: boolean;
    showAssignFieldsButton?: boolean;
    assignFieldsButtonId?: string;
    nodeScope?: NodeScopeFilters;
}) => {
    const country = useSelector(getSessionCountry);
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const [assignFieldsModalOpen, setAssignFieldsModalOpen] = useState(false);

    const units = useUnitProvider();
    const fieldsDashboardQuery = useFieldsDashboard({
        country: country.code,
        node: userNodeId,
        ...nodeScope,
    });

    const formattedArea = useMemo(
        () =>
            _('Superficie total: {{value}}', {
                value: convertAreaFromM2(fieldsDashboardQuery.data?.totalArea || 0, units).toString(),
            }),
        [units, fieldsDashboardQuery.data?.totalArea]
    );

    const handleClickNewField = () => {
        setNewFieldModalOpen(true);
    };

    return (
        <>
            <DashboardCard
                title={_('Lotes')}
                subtitle={formattedArea}
                isLoading={fieldsDashboardQuery.isLoading}
                buttonProps={
                    showNewFieldButton
                        ? {
                              children: _('Agregar lote'),
                              onClick: handleClickNewField,
                              color: 'secondary',
                          }
                        : undefined
                }
                secondaryButtonProps={
                    showAssignFieldsButton
                        ? {
                              children: _('Asignar lotes a un productor'),
                              onClick: () => setAssignFieldsModalOpen(true),
                              color: 'secondary',
                              ...(assignFieldsButtonId ? { id: assignFieldsButtonId } : {}),
                          }
                        : undefined
                }
                number={fieldsDashboardQuery.data?.count}
            />
            <NewFieldGrowersModal
                open={newFieldModalOpen}
                setOpen={setNewFieldModalOpen}
                rvUserNodeId={userNodeId}
                growerUserNodeId={nodeScope.includeChildren ? undefined : userNodeId}
                trackEntrySource='fields-dashboard-card'
            />
            <AssignFieldModal open={assignFieldsModalOpen} setOpen={setAssignFieldsModalOpen} />
        </>
    );
};

export default FieldsDashboardCard;
